import { Button, ButtonProps, CircularProgress, Tooltip } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import ThemeTypography from '../../Primitives/Typography/ThemeTypography';
import { ThemeTypographyColor } from '../../utils/designSystemUtils';
import React, { useEffect } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';

const getButtonHeight = (size: IProps['size']) => {
  switch (size) {
    case 'large':
      return 36;
    case 'medium':
      return 30;
    case 'small':
      return 22;
    default:
      break;
  }
};

const getButtonPadding = (size: IProps['size'], theme: Theme) => {
  switch (size) {
    case 'large':
      return theme.spacing(0, 4);
    case 'medium':
      return theme.spacing(0, 4);
    case 'small':
      return theme.spacing(0, 1);
    default:
      break;
  }
};

const getButtonColors = (
  color: TButtonColor,
  theme: Theme
): {
  background: string;
  color: string;
  hover: {
    background: string;
    color?: string;
  };
} => {
  switch (color) {
    case 'PRIMARY':
      return {
        background: theme.palette.primary.main,
        color: 'inherit',
        hover: {
          // background: theme.palette.primary.light,
          background: '#5B9FFF',
        },
      };
    case 'YELLOW':
      return {
        background: theme.custom.themeColors.accent[100],
        color: 'inherit',
        hover: {
          background: '#FFCC47',
        },
      };
    case 'YELLOW_40':
      return {
        background: theme.custom.themeColors.accent[40],
        color: 'inherit',
        hover: {
          background: theme.custom.themeColors.accent[60],
        },
      };
    case 'BLUE_ICE':
      return {
        background: theme.custom.colors.backgroundLight,
        color: 'inherit',
        hover: {
          background: theme.custom.colors.backgroundLight,
          color: theme.custom.colors.secondaryLighter,
        },
      };
    case 'WHITE':
      return {
        background: theme.palette.common.white,
        color: theme.palette.secondary.main,
        hover: {
          background: theme.custom.themeColors.grayScale[20],
        },
      };
    default:
      return {
        background: theme.palette.primary.main,
        color: 'inherit',
        hover: {
          background: theme.palette.primary.light,
        },
      };
  }
};

const StyledButton = styled(Button)<{
  size: IProps['size'];
  'button-color': TButtonColor;
  padding?: string | number;
  'auto-overflow'?: Tbooleanish;
}>(({ theme, size, 'button-color': buttonColor, padding, 'auto-overflow': autoOverflow }) => ({
  overflow: 'hidden',
  padding: padding ? padding : getButtonPadding(size, theme),
  height: getButtonHeight(size),
  backgroundColor: getButtonColors(buttonColor, theme).background,
  color: getButtonColors(buttonColor, theme).color,

  '&:hover': {
    ...getButtonColors(buttonColor, theme).hover,

    // Button text is wrapped in a p, so we need to target it
    '& .MuiTypography-root': {
      color: `${getButtonColors(buttonColor, theme).hover.color} !important`,
    },
  },
  ...(autoOverflow === 'true'
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    : {}),
}));

const DisabledContainer = styled('span')(() => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: '100%',
}));

const ProgressContainer = styled('div')<{ visible: boolean }>(({ theme, visible }) => ({
  position: 'absolute',
  marginLeft: 'auto',
  marginRight: 'auto',
  left: '0',
  right: '0',
  opacity: 0,
  transform: 'translateY(30px)',
  transition: 'transform 0.125s ease-in',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  '& > *': {
    color: theme.palette.common.white,
  },

  ...(visible && {
    opacity: 1,
    transform: 'translateY(0)',
  }),
}));

interface ILabelProps {
  visible: boolean;
}

const Label = styled('span')<ILabelProps>(({ visible }) => {
  return {
    transition: 'all 0.125s ease-in',
    opacity: 1,
    transform: 'translateY(0)',
    lineHeight: 1,

    ...(!visible && {
      opacity: 0,
      transform: 'translateY(-30px)',
    }),
  };
});

export type TButtonColor = 'YELLOW' | 'BLUE_ICE' | 'WHITE' | 'YELLOW_40' | 'PRIMARY';

interface IProps {
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: TButtonColor;
  typographyColor?: ThemeTypographyColor;
  tooltip?: string;
  padding?: string | number;
  target?: '_blank' | '_self';
  href?: string;
  autoOverflow?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent) => void;
  'data-testid'?: string;
}

const ThemeButton: React.FC<IProps & Omit<ButtonProps, 'color'>> = ({
  children,
  loading = false,
  disabled,
  onClick,
  type,
  variant = 'contained',
  size = 'medium',
  color = 'PRIMARY',
  typographyColor,
  tooltip = '',
  padding,
  autoOverflow,
  startIcon,
  'data-testid': testId,
  ...rest
}) => {
  const defaultTextColor = typographyColor
    ? typographyColor
    : color === 'PRIMARY'
    ? 'WHITE'
    : 'GRAY_100';
  const triggerSubmit = (event: KeyboardEvent) => {
    if (type === 'submit' && event.key === 'Enter' && typeof onClick === 'function') {
      onClick(event);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', triggerSubmit);

    return () => window.removeEventListener('keydown', triggerSubmit);
  }, []); // eslint-disable-line

  return (
    <Tooltip title={disabled || loading ? tooltip : ''}>
      <DisabledContainer>
        <StyledButton
          type={type}
          onClick={onClick}
          disabled={loading || disabled}
          variant={variant}
          size={size}
          button-color={color}
          padding={padding}
          auto-overflow={Booleanish(autoOverflow)}
          startIcon={!loading ? startIcon : undefined}
          data-testid={testId}
          {...rest}
        >
          <ProgressContainer visible={loading}>
            <CircularProgress size={16} thickness={5} />
          </ProgressContainer>
          <Label visible={!loading}>
            <ThemeTypography
              variant={size === 'large' ? 'BUTTON_LARGE' : 'BUTTON_SMALL'}
              color={disabled ? 'GRAY_40' : defaultTextColor}
              autoOverflow
            >
              {children}
            </ThemeTypography>
          </Label>
        </StyledButton>
      </DisabledContainer>
    </Tooltip>
  );
};

export default ThemeButton;
